import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { promptService } from '../services/promptService';
import type {
  CreatePromptData,
  Prompt,
  PromptDetail,
  PromptType,
  PromptVersion,
  UpdatePromptData,
} from '../types';

// ── Design tokens ─────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<PromptType, { label: string; color: string; bg: string }> = {
  system: { label: 'System', color: '#1565C0', bg: alpha('#1565C0', 0.08) },
  user:   { label: 'User',   color: '#00695C', bg: alpha('#00695C', 0.08) },
};

// ── Tag input ─────────────────────────────────────────────────────────────────

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

const TagInput: React.FC<TagInputProps> = ({ tags, onChange, placeholder = 'Add tag, press Enter' }) => {
  const [input, setInput] = useState('');

  const add = (raw: string) => {
    const tag = raw.trim().toLowerCase().replace(/\s+/g, '-');
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(input); }
    if (e.key === 'Backspace' && !input && tags.length) onChange(tags.slice(0, -1));
  };

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => input && add(input)}
        placeholder={tags.length === 0 ? placeholder : ''}
        InputProps={{
          startAdornment: tags.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mr: 0.5, py: 0.5 }}>
              {tags.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  onDelete={() => onChange(tags.filter((x) => x !== t))}
                  sx={{ height: 20, fontSize: 11 }}
                />
              ))}
            </Box>
          ) : undefined,
        }}
        sx={{ '& .MuiOutlinedInput-root': { flexWrap: 'wrap', alignItems: 'center' } }}
      />
      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
        Press Enter or comma to add a tag
      </Typography>
    </Box>
  );
};

// ── Create / Edit dialog ──────────────────────────────────────────────────────

interface PromptDialogProps {
  prompt?: Prompt;
  onClose: () => void;
}

const PromptDialog: React.FC<PromptDialogProps> = ({ prompt, onClose }) => {
  const isEdit = !!prompt;
  const queryClient = useQueryClient();

  const [name, setName]               = useState(prompt?.name ?? '');
  const [key, setKey]                 = useState(prompt?.key ?? '');
  const [description, setDescription] = useState(prompt?.description ?? '');
  const [content, setContent]         = useState(prompt?.content ?? '');
  const [promptType, setPromptType]   = useState<PromptType>(prompt?.prompt_type ?? 'system');
  const [tags, setTags]               = useState<string[]>(prompt?.tags ?? []);
  const [changeNote, setChangeNote]   = useState('');
  const [keyLoading, setKeyLoading]   = useState(false);

  const handleNameBlur = async () => {
    if (isEdit || key || !name) return;
    setKeyLoading(true);
    try {
      const suggested = await promptService.suggestKey(name);
      setKey(suggested);
    } finally {
      setKeyLoading(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: CreatePromptData) => promptService.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['prompts'] }); onClose(); },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdatePromptData) => promptService.update(prompt!.key, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['prompts'] }); onClose(); },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isError   = createMutation.isError   || updateMutation.isError;

  const handleSave = () => {
    if (isEdit) {
      updateMutation.mutate({ content, tags, name, description, change_note: changeNote });
    } else {
      createMutation.mutate({ key, name, description, content, prompt_type: promptType, tags });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {isEdit ? `Edit — ${prompt.name}` : 'New Prompt'}
        </Typography>
        {prompt?.description && (
          <Typography variant="caption" color="text.secondary">{prompt.description}</Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {!isEdit && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              required
            />
            <TextField
              label="Key"
              fullWidth
              size="small"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              required
              InputProps={{
                endAdornment: keyLoading ? (
                  <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                ) : undefined,
                sx: { fontFamily: 'monospace', fontSize: 13 },
              }}
              helperText="Unique identifier used in code"
            />
          </Box>
        )}

        {!isEdit && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Select
              size="small"
              value={promptType}
              onChange={(e) => setPromptType(e.target.value as PromptType)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="system">System</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </Box>
        )}

        {isEdit && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Tags
          </Typography>
          <TagInput tags={tags} onChange={setTags} />
        </Box>

        <TextField
          label="Prompt content"
          multiline
          minRows={14}
          maxRows={28}
          fullWidth
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 } }}
        />

        {isEdit && (
          <TextField
            label="Change note (optional)"
            fullWidth
            size="small"
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            placeholder="Briefly describe what you changed"
          />
        )}

        {isError && <Alert severity="error">Failed to save. Please try again.</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isPending || !content.trim() || (!isEdit && (!key.trim() || !name.trim()))}
        >
          {isPending ? <CircularProgress size={18} /> : isEdit ? 'Save changes' : 'Create prompt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── History dialog ────────────────────────────────────────────────────────────

interface HistoryDialogProps {
  promptKey: string | null;
  onClose: () => void;
}

const HistoryDialog: React.FC<HistoryDialogProps> = ({ promptKey, onClose }) => {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery<PromptDetail>({
    queryKey: ['prompts', promptKey],
    queryFn: () => promptService.getOne(promptKey!),
    enabled: !!promptKey,
  });

  const rollback = useMutation({
    mutationFn: (version: number) => promptService.rollback(promptKey!, version),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['prompts'] }); onClose(); },
  });

  if (!promptKey) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>Version History</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {promptKey}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {isLoading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        ) : !data?.history?.length ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No prior versions saved yet.</Typography>
            <Typography variant="caption" color="text.disabled">
              Versions are created each time you save changes.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {data.history.map((v: PromptVersion) => (
              <Box key={v.id}>
                <ListItemButton
                  onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                  sx={{ borderRadius: 1, py: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={`v${v.version}`} size="small" sx={{ height: 18, fontSize: 11 }} />
                        <Typography variant="body2">
                          {new Date(v.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                    secondary={v.change_note || 'No change note'}
                  />
                  <Tooltip title="Restore this version">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); rollback.mutate(v.version); }}
                      disabled={rollback.isPending}
                      sx={{ mr: 1 }}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {expanded === v.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </ListItemButton>
                <Collapse in={expanded === v.id}>
                  <Box
                    sx={{
                      mx: 1, mb: 1, p: 1.5,
                      bgcolor: 'background.default',
                      border: '1px solid', borderColor: 'divider',
                      borderRadius: 1,
                      fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      maxHeight: 260, overflowY: 'auto',
                    }}
                  >
                    {v.content}
                  </Box>
                </Collapse>
                <Divider />
              </Box>
            ))}
          </List>
        )}
        {rollback.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>Rollback failed. Please try again.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Prompt card ───────────────────────────────────────────────────────────────

interface PromptCardProps {
  prompt: Prompt;
  onEdit: (p: Prompt) => void;
  onHistory: (key: string) => void;
  onDelete: (p: Prompt) => void;
}

const PromptCard: React.FC<PromptCardProps> = ({ prompt, onEdit, onHistory, onDelete }) => {
  const [open, setOpen] = useState(false);
  const cfg = TYPE_CONFIG[prompt.prompt_type] ?? TYPE_CONFIG.system;
  const tags: string[] = Array.isArray(prompt.tags) ? prompt.tags : [];

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        borderLeft: `3px solid ${cfg.color}`,
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      {/* Card header */}
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5,
          px: 2, py: 1.5, cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {/* Left: name + key + description + tags */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
              {prompt.name}
            </Typography>
            <Chip
              label={cfg.label}
              size="small"
              sx={{
                height: 18, fontSize: 10, fontWeight: 600,
                color: cfg.color, bgcolor: cfg.bg, border: 'none',
              }}
            />
            <Typography variant="caption" color="text.disabled">v{prompt.version}</Typography>
          </Box>

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontFamily: 'monospace', display: 'block', mb: 0.5 }}
          >
            {prompt.key}
          </Typography>

          {prompt.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {prompt.description}
            </Typography>
          )}

          {tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {tags.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10, borderRadius: 1 }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Right: action buttons */}
        <Box
          sx={{ display: 'flex', gap: 0.25, flexShrink: 0, alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(prompt)}>
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="History">
            <IconButton size="small" onClick={() => onHistory(prompt.key)}>
              <HistoryIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(prompt)}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" sx={{ ml: 0.5 }}>
            {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Expandable content */}
      <Collapse in={open}>
        <Box
          sx={{
            mx: 2, mb: 2,
            p: 1.5,
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
            border: '1px solid', borderColor: 'divider',
            borderRadius: 1,
            fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: 420, overflowY: 'auto',
            color: 'text.primary',
          }}
        >
          {prompt.content}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── Delete confirm dialog ─────────────────────────────────────────────────────

interface DeleteDialogProps {
  prompt: Prompt | null;
  onClose: () => void;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({ prompt, onClose }) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => promptService.delete(prompt!.key),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['prompts'] }); onClose(); },
  });
  if (!prompt) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete prompt?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          <strong>{prompt.name}</strong> will be permanently deleted.
          If this is a built-in prompt, the code will fall back to its hardcoded default.
        </Typography>
        {mutation.isError && <Alert severity="error" sx={{ mt: 1 }}>Delete failed.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? <CircularProgress size={18} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

export const PromptManagement: React.FC = () => {
  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | PromptType>('all');
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [editPrompt, setEditPrompt] = useState<Prompt | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const [deletePrompt, setDeletePrompt] = useState<Prompt | null>(null);

  const { data: prompts = [], isLoading, isError } = useQuery<Prompt[]>({
    queryKey: ['prompts'],
    queryFn: promptService.getAll,
  });

  // Collect all unique tags across prompts.
  const allTags = useMemo(() => {
    const set = new Set<string>();
    prompts.forEach((p) => {
      const tags: string[] = Array.isArray(p.tags) ? p.tags : [];
      tags.forEach((t) => set.add(t));
    });
    return Array.from(set).sort();
  }, [prompts]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return prompts.filter((p) => {
      const matchesSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.key.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q);
      const matchesType = typeFilter === 'all' || p.prompt_type === typeFilter;
      const tags: string[] = Array.isArray(p.tags) ? p.tags : [];
      const matchesTags = activeTags.length === 0 || activeTags.every((t) => tags.includes(t));
      return matchesSearch && matchesType && matchesTags;
    });
  }, [prompts, search, typeFilter, activeTags]);

  const toggleTag = (tag: string) =>
    setActiveTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, py: 3 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">
            Prompt Library
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            {prompts.length} prompts · edit, tag, and version-control AI instructions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          New Prompt
        </Button>
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name, key, or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            onChange={(_, v) => v && setTypeFilter(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, textTransform: 'none', fontWeight: 500 } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="system">System</ToggleButton>
            <ToggleButton value="user">User</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {allTags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>Tags:</Typography>
            {allTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onClick={() => toggleTag(tag)}
                variant={activeTags.includes(tag) ? 'filled' : 'outlined'}
                color={activeTags.includes(tag) ? 'primary' : 'default'}
                sx={{ height: 22, fontSize: 11, cursor: 'pointer', borderRadius: 1 }}
              />
            ))}
            {activeTags.length > 0 && (
              <Button size="small" sx={{ fontSize: 11, p: 0, minWidth: 0, ml: 0.5 }} onClick={() => setActiveTags([])}>
                Clear
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* ── Prompt list ── */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load prompts.</Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography color="text.secondary">No prompts match your filters.</Typography>
          {(search || typeFilter !== 'all' || activeTags.length > 0) && (
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => { setSearch(''); setTypeFilter('all'); setActiveTags([]); }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filtered.map((p) => (
            <PromptCard
              key={p.key}
              prompt={p}
              onEdit={setEditPrompt}
              onHistory={setHistoryKey}
              onDelete={setDeletePrompt}
            />
          ))}
        </Box>
      )}

      {/* ── Dialogs ── */}
      {createOpen && <PromptDialog onClose={() => setCreateOpen(false)} />}
      {editPrompt  && <PromptDialog prompt={editPrompt} onClose={() => setEditPrompt(null)} />}
      <HistoryDialog promptKey={historyKey} onClose={() => setHistoryKey(null)} />
      <DeleteDialog prompt={deletePrompt} onClose={() => setDeletePrompt(null)} />
    </Box>
  );
};
